import type { Schemas } from '@argzombie/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type FormEvent } from 'react';
import { PageHeader } from '../components/PageHeader';
import { errorText, ok, useApi } from '../lib/api';
import { isoToLocalInput, localInputToIso, when } from '../lib/datetime';
import {
  CHANNEL_KINDS,
  CHANNEL_NAME,
  CODE_STATES,
  batchCsv,
  channelHasName,
  channelOf,
  downloadBlob,
  posterHtml,
  qrPngDataUrl,
  qrSvg,
  toCsv,
  type ChannelKind,
} from '../lib/triggerCodes';
import { useTriggerOptions, type TriggerOption } from '../lib/useTriggerOptions';

type Row = Schemas['AdminTriggerCodeRow'];
type Geofence = Schemas['AdminTriggerGeofence'];
type BatchResult = Schemas['AdminTriggerCodeBatchResult'];

const PAGE = 50;

const input = 'rounded border-2 border-ink bg-white px-2 py-1';
const btn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/40 disabled:opacity-40';
const primary =
  'rounded border-2 border-ink bg-benday px-3 py-1 text-sm font-semibold disabled:opacity-50';
const danger =
  'rounded border-2 border-ink bg-blam px-3 py-1 text-sm font-semibold text-white disabled:opacity-50';
const card = 'rounded border-2 border-ink bg-white p-4 text-sm';

/** State plates: cyan = working, yellow = not yet, red = retired or used up (the admin's plate meanings). */
const stateCls: Record<string, string> = {
  active: 'bg-benday',
  not_yet: 'bg-caption',
  used_up: 'bg-blam text-white',
  retired: 'bg-blam text-white',
};

function windowText(r: Row) {
  if (!r.activeFrom && !r.activeUntil) return 'always';
  return `${r.activeFrom ? when(r.activeFrom) : '…'} → ${r.activeUntil ? when(r.activeUntil) : '…'}`;
}

/** Optional numbers typed as text: '' = null, otherwise a finite number or a problem. */
function parseOptional(
  text: string,
  what: string,
  integer = false,
): [number | null, string | null] {
  if (text.trim() === '') return [null, null];
  const n = Number(text);
  if (!Number.isFinite(n) || (integer && !Number.isInteger(n)))
    return [null, `${what} must be a number.`];
  return [n, null];
}

type GeoText = { on: boolean; lat: string; lon: string; radiusM: string };
const noGeo: GeoText = { on: false, lat: '', lon: '', radiusM: '' };
const geoText = (g: Geofence | null | undefined): GeoText =>
  g ? { on: true, lat: String(g.lat), lon: String(g.lon), radiusM: String(g.radiusM) } : noGeo;

function parseGeo(g: GeoText): [Geofence | null, string | null] {
  if (!g.on) return [null, null];
  const lat = Number(g.lat);
  const lon = Number(g.lon);
  const radiusM = Number(g.radiusM);
  if (!(g.lat.trim() && lat >= -90 && lat <= 90) || !(g.lon.trim() && lon >= -180 && lon <= 180))
    return [null, 'The geofence needs lat -90..90 and lon -180..180.'];
  if (!(radiusM > 0)) return [null, 'The geofence needs a radius above 0 m.'];
  return [{ lat, lon, radiusM }, null];
}

function GeoInputs({
  value,
  onChange,
  label,
}: {
  value: GeoText;
  onChange: (g: GeoText) => void;
  label: string;
}) {
  return (
    <div className="col-span-2 flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1 text-xs font-semibold">
        <input
          type="checkbox"
          aria-label={`${label} geofence`}
          checked={value.on}
          onChange={(e) => onChange({ ...value, on: e.target.checked })}
        />
        Geofence
      </label>
      {value.on &&
        (['lat', 'lon', 'radiusM'] as const).map((k) => (
          <input
            key={k}
            aria-label={`${label} geofence ${k}`}
            placeholder={k === 'radiusM' ? 'radius m' : k}
            inputMode="decimal"
            value={value[k]}
            onChange={(e) => onChange({ ...value, [k]: e.target.value })}
            className={`${input} w-28 font-mono`}
          />
        ))}
    </div>
  );
}

function WindowInputs({
  from,
  until,
  onChange,
  label,
}: {
  from: string;
  until: string;
  onChange: (from: string, until: string) => void;
  label: string;
}) {
  return (
    <>
      <label className="flex flex-col text-xs font-semibold">
        Active from (optional)
        <input
          aria-label={`${label} active from`}
          type="datetime-local"
          value={from}
          onChange={(e) => onChange(e.target.value, until)}
          className={`${input} font-normal`}
        />
      </label>
      <label className="flex flex-col text-xs font-semibold">
        Active until (optional)
        <input
          aria-label={`${label} active until`}
          type="datetime-local"
          value={until}
          onChange={(e) => onChange(from, e.target.value)}
          className={`${input} font-normal`}
        />
      </label>
    </>
  );
}

function TriggerSelect({
  value,
  onChange,
  options,
  label,
  any,
}: {
  value: string;
  onChange: (v: string) => void;
  options: TriggerOption[];
  label: string;
  any?: string;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${input} col-span-2`}
    >
      <option value="">{any ?? '(choose a trigger)'}</option>
      {options.map((t) => (
        <option key={t.value} value={t.value}>
          {t.label}
        </option>
      ))}
    </select>
  );
}

const Problem = ({ text }: { text: string | null }) =>
  text ? (
    <p className="mt-2 text-blam" role="alert">
      {text}
    </p>
  ) : null;

/** Every code of a batch as CSV (pages through the list, 200 at a time). */
async function exportBatch(client: ReturnType<typeof useApi>['client'], batchId: string) {
  const rows: Row[] = [];
  for (let offset = 0; ; offset += 200) {
    const page = ok(
      await client.GET('/admin/trigger-codes', {
        params: { query: { batchId, offset, take: 200 } },
      }),
    );
    rows.push(...page.items);
    if (page.items.length === 0 || rows.length >= page.total) break;
  }
  const csv = toCsv(
    ['code', 'scan_url', 'trigger_id', 'channel', 'batch_id', 'state', 'redemptions'],
    rows.map((r) => [
      r.code,
      r.scanUrl,
      r.triggerId,
      r.channel,
      batchId,
      r.state,
      r.redemptionCount,
    ]),
  );
  downloadBlob(new Blob([csv], { type: 'text/csv' }), `batch-${batchId}.csv`);
}

/**
 * Trigger codes (ADR 0029 §12): the codes that point at a trigger, per channel (web, admin, poster, event) or as a batch
 * of single-use cards. Filter, create one or a batch (CSV for printing), edit a code's window, max uses and geofence,
 * revoke one, a batch or a season, and print: copy the URL, QR SVG/PNG, or an A4 poster. Replaces the Boss codes page.
 */
export function TriggerCodesPage() {
  const { client, session } = useApi();
  const qc = useQueryClient();
  const [filters, setFilters] = useState({
    triggerId: '',
    channel: '',
    season: '',
    state: '',
    batchId: '',
    code: '',
  });
  const [offset, setOffset] = useState(0);
  const filter = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch });
    setOffset(0);
  };

  const codes = useQuery({
    queryKey: ['admin-trigger-codes', filters, offset],
    queryFn: async () =>
      ok(
        await client.GET('/admin/trigger-codes', {
          params: {
            query: {
              triggerId: filters.triggerId || undefined,
              channel: filters.channel || undefined,
              season: filters.season || undefined,
              state: filters.state || undefined,
              batchId: filters.batchId || undefined,
              code: filters.code || undefined,
              offset,
              take: PAGE,
            },
          },
        }),
      ),
  });
  const triggers = useTriggerOptions(
    (codes.data?.items ?? []).map((r) => ({ triggerId: r.triggerId, title: r.triggerTitle })),
  );
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin-trigger-codes'] });
  const season = codes.data?.season ?? '';

  return (
    <>
      <PageHeader
        title="Trigger codes"
        description="The QR codes that point at a scan trigger: one per channel (web, poster, event), or a batch of single-use cards. Every change is audited."
      />
      {!session.isAdmin && (
        <p className="mb-3 text-sm text-bruise">
          Creating, editing and revoking codes is for Admins.
        </p>
      )}
      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <select
          aria-label="Trigger filter"
          value={filters.triggerId}
          onChange={(e) => filter({ triggerId: e.target.value })}
          className={input}
        >
          <option value="">any trigger</option>
          {triggers.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input
          aria-label="Channel filter"
          placeholder="channel (web, poster:name, card:…)"
          value={filters.channel}
          onChange={(e) => filter({ channel: e.target.value.trim() })}
          className={`${input} w-56 font-mono`}
        />
        <input
          aria-label="Season filter"
          placeholder={`season (current: ${season || '…'})`}
          value={filters.season}
          onChange={(e) => filter({ season: e.target.value.trim() })}
          className={`${input} w-48 font-mono`}
        />
        <select
          aria-label="State filter"
          value={filters.state}
          onChange={(e) => filter({ state: e.target.value })}
          className={input}
        >
          <option value="">any state</option>
          {CODE_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          aria-label="Code search"
          placeholder="code contains…"
          value={filters.code}
          onChange={(e) => filter({ code: e.target.value.trim() })}
          className={`${input} w-44 font-mono`}
        />
        {filters.batchId && (
          <span className="flex items-center gap-2 rounded border-2 border-ink bg-caption px-2">
            batch <span className="font-mono">{filters.batchId.slice(0, 8)}</span>
            <button
              type="button"
              className={btn}
              onClick={() => void exportBatch(client, filters.batchId)}
            >
              Batch CSV
            </button>
            <button type="button" className={btn} onClick={() => filter({ batchId: '' })}>
              Clear
            </button>
          </span>
        )}
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <CreateForm triggers={triggers} season={season} onCreated={refresh} />
        <BatchForm
          triggers={triggers}
          season={season}
          onCreated={(batchId) => {
            refresh();
            filter({ batchId });
          }}
        />
        <RevokeBatchForm batchId={filters.batchId} onRevoked={refresh} />
        <RevokeSeasonForm season={season} onRevoked={refresh} />
      </div>

      {codes.isPending ? (
        <p>Loading&hellip;</p>
      ) : codes.isError ? (
        <p className="text-blam" role="alert">
          {errorText(codes.error)}
        </p>
      ) : (
        <>
          <CodesTable
            rows={codes.data.items}
            triggers={triggers}
            onChanged={refresh}
            onBatch={(batchId) => filter({ batchId })}
          />
          <div className="mt-2 flex items-center gap-2 text-xs text-bruise">
            <span>
              {codes.data.total === 0
                ? ''
                : `${offset + 1}–${Math.min(offset + PAGE, codes.data.total)} of ${codes.data.total}, newest first.`}
            </span>
            <button
              type="button"
              className={btn}
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
            >
              Newer
            </button>
            <button
              type="button"
              className={btn}
              disabled={offset + PAGE >= codes.data.total}
              onClick={() => setOffset(offset + PAGE)}
            >
              Older
            </button>
          </div>
        </>
      )}
    </>
  );
}

function CreateForm({
  triggers,
  season,
  onCreated,
}: {
  triggers: TriggerOption[];
  season: string;
  onCreated: () => void;
}) {
  const { client } = useApi();
  const [form, setForm] = useState({
    triggerId: '',
    kind: 'poster' as ChannelKind,
    name: '',
    season: null as string | null,
    activeFrom: '',
    activeUntil: '',
    maxUses: '',
    geo: noGeo,
    reason: '',
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [created, setCreated] = useState<Row | null>(null);

  const create = useMutation({
    mutationFn: async (body: Schemas['AdminTriggerCodeCreate']) =>
      ok(await client.POST('/admin/trigger-codes', { body })),
    onSuccess: (row) => {
      setProblem(null);
      setCreated(row);
      setForm({ ...form, name: '', reason: '' });
      onCreated();
    },
    onError: (e) => setProblem(errorText(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setCreated(null);
    if (!form.triggerId) return setProblem('Pick a trigger.');
    if (channelHasName(form.kind) && !CHANNEL_NAME.test(form.name.trim()))
      return setProblem('The channel name is 1-48 of a-z, 0-9, _ and -.');
    const [maxRedemptions, maxProblem] = parseOptional(form.maxUses, 'Max uses', true);
    if (maxProblem) return setProblem(maxProblem);
    if (maxRedemptions !== null && maxRedemptions < 1) return setProblem('Max uses is at least 1.');
    const [geofence, geoProblem] = parseGeo(form.geo);
    if (geoProblem) return setProblem(geoProblem);
    create.mutate({
      triggerId: form.triggerId,
      channel: channelOf(form.kind, form.name),
      season: (form.season ?? season) || null,
      activeFrom: localInputToIso(form.activeFrom),
      activeUntil: localInputToIso(form.activeUntil),
      maxRedemptions,
      geofence,
      reason: form.reason || null,
    });
  };

  return (
    <form onSubmit={submit} className={card}>
      <h2 className="mb-2 font-semibold">New code</h2>
      <div className="grid grid-cols-2 gap-2">
        <TriggerSelect
          label="Trigger"
          value={form.triggerId}
          options={triggers}
          onChange={(triggerId) => setForm({ ...form, triggerId })}
        />
        <select
          aria-label="Channel"
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value as ChannelKind })}
          className={input}
        >
          {CHANNEL_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <input
          aria-label="Channel name"
          placeholder={channelHasName(form.kind) ? 'name, e.g. downtown_cafe' : '(no name)'}
          disabled={!channelHasName(form.kind)}
          value={channelHasName(form.kind) ? form.name : ''}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={`${input} font-mono disabled:opacity-50`}
        />
        <label className="flex items-center gap-2">
          Season
          <input
            aria-label="Season"
            value={form.season ?? season}
            onChange={(e) => setForm({ ...form, season: e.target.value })}
            className={`${input} min-w-0 flex-1 font-mono`}
          />
        </label>
        <label className="flex items-center gap-2">
          Max uses
          <input
            aria-label="Max uses"
            placeholder="no cap"
            inputMode="numeric"
            value={form.maxUses}
            onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
            className={`${input} min-w-0 flex-1 font-mono`}
          />
        </label>
        <WindowInputs
          label="New code"
          from={form.activeFrom}
          until={form.activeUntil}
          onChange={(activeFrom, activeUntil) => setForm({ ...form, activeFrom, activeUntil })}
        />
        <GeoInputs
          label="New code"
          value={form.geo}
          onChange={(geo) => setForm({ ...form, geo })}
        />
        <input
          aria-label="Create reason"
          placeholder="reason (for the audit log)"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          className={`${input} col-span-2`}
        />
      </div>
      <p className="mt-1 text-xs text-bruise">
        The trigger must be in the published content. A code only narrows the trigger&apos;s rules.
      </p>
      <div className="mt-2 flex items-center gap-3">
        <button type="submit" disabled={create.isPending} className={primary}>
          {create.isPending ? 'Creating…' : 'Create code'}
        </button>
        {created && (
          <span>
            Created <span className="font-mono font-semibold">{created.code}</span>
          </span>
        )}
      </div>
      <Problem text={problem} />
    </form>
  );
}

function BatchForm({
  triggers,
  season,
  onCreated,
}: {
  triggers: TriggerOption[];
  season: string;
  onCreated: (batchId: string) => void;
}) {
  const { client } = useApi();
  const [form, setForm] = useState({
    triggerId: '',
    count: '100',
    name: '',
    maxUses: '1',
    activeFrom: '',
    activeUntil: '',
    reason: '',
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<(BatchResult & { triggerId: string }) | null>(null);

  const csv = (r: BatchResult & { triggerId: string }) =>
    downloadBlob(
      new Blob([batchCsv(r)], { type: 'text/csv' }),
      `${r.channel.replace(':', '-')}.csv`,
    );

  const create = useMutation({
    mutationFn: async (body: Schemas['AdminTriggerCodeBatch']) =>
      ok(await client.POST('/admin/trigger-codes/batch', { body })),
    onSuccess: (r, body) => {
      setProblem(null);
      const withTrigger = { ...r, triggerId: body.triggerId };
      setResult(withTrigger);
      csv(withTrigger);
      setForm({ ...form, name: '', reason: '' });
      onCreated(r.batchId);
    },
    onError: (e) => setProblem(errorText(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setResult(null);
    if (!form.triggerId) return setProblem('Pick a trigger.');
    const count = Number(form.count);
    if (!Number.isInteger(count) || count < 1 || count > 1000)
      return setProblem('A batch is 1 to 1000 codes.');
    const maxUses = Number(form.maxUses);
    if (!Number.isInteger(maxUses) || maxUses < 1)
      return setProblem('Each card needs at least one use.');
    if (form.name.trim() && !CHANNEL_NAME.test(form.name.trim()))
      return setProblem('The batch name is 1-48 of a-z, 0-9, _ and -.');
    create.mutate({
      triggerId: form.triggerId,
      count,
      batchName: form.name.trim() || null,
      maxRedemptions: maxUses,
      season: season || null,
      activeFrom: localInputToIso(form.activeFrom),
      activeUntil: localInputToIso(form.activeUntil),
      geofence: null,
      reason: form.reason || null,
    });
  };

  return (
    <form onSubmit={submit} className={card}>
      <h2 className="mb-2 font-semibold">Batch of single-use cards</h2>
      <div className="grid grid-cols-2 gap-2">
        <TriggerSelect
          label="Batch trigger"
          value={form.triggerId}
          options={triggers}
          onChange={(triggerId) => setForm({ ...form, triggerId })}
        />
        <label className="flex items-center gap-2">
          Cards
          <input
            aria-label="Batch count"
            inputMode="numeric"
            value={form.count}
            onChange={(e) => setForm({ ...form, count: e.target.value })}
            className={`${input} min-w-0 flex-1 font-mono`}
          />
        </label>
        <label className="flex items-center gap-2">
          Uses each
          <input
            aria-label="Batch uses per card"
            inputMode="numeric"
            value={form.maxUses}
            onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
            className={`${input} min-w-0 flex-1 font-mono`}
          />
        </label>
        <input
          aria-label="Batch name"
          placeholder="batch name (channel card:<name>), optional"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={`${input} col-span-2 font-mono`}
        />
        <WindowInputs
          label="Batch"
          from={form.activeFrom}
          until={form.activeUntil}
          onChange={(activeFrom, activeUntil) => setForm({ ...form, activeFrom, activeUntil })}
        />
        <input
          aria-label="Batch reason"
          placeholder="reason (for the audit log)"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          className={`${input} col-span-2`}
        />
      </div>
      <p className="mt-1 text-xs text-bruise">
        Up to 1000 codes in the current season, channel card:&lt;name&gt;. The CSV (code, scan URL)
        downloads when they&apos;re made.
      </p>
      <div className="mt-2 flex items-center gap-3">
        <button type="submit" disabled={create.isPending} className={primary}>
          {create.isPending ? 'Creating…' : 'Create batch'}
        </button>
        {result && (
          <span>
            {result.codes.length} codes in <span className="font-mono">{result.channel}</span>{' '}
            <button type="button" className={btn} onClick={() => csv(result)}>
              Download CSV
            </button>
          </span>
        )}
      </div>
      <Problem text={problem} />
    </form>
  );
}

/** A revoke with a required reason and a confirm(), shared by the batch and season forms. */
function RevokeForm({
  title,
  hint,
  target,
  targetLabel,
  placeholder,
  confirmText,
  run,
  onRevoked,
}: {
  title: string;
  hint: string;
  target: string;
  targetLabel: string;
  placeholder: string;
  confirmText: (target: string) => string;
  run: (target: string, reason: string) => Promise<{ revoked: number }>;
  onRevoked: () => void;
}) {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const current = value || target;
  const revoke = useMutation({
    mutationFn: async () => run(current.trim(), reason),
    onSuccess: (r) => {
      setProblem(null);
      setDone(`Revoked ${r.revoked} code(s).`);
      setValue('');
      setReason('');
      onRevoked();
    },
    onError: (e) => setProblem(errorText(e)),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setDone(null);
        if (!current.trim()) return setProblem(`Name the ${targetLabel}.`);
        if (!reason.trim()) return setProblem('A reason is required.');
        if (!confirm(confirmText(current.trim()))) return;
        revoke.mutate();
      }}
      className={card}
    >
      <h2 className="mb-2 font-semibold">{title}</h2>
      <p className="mb-2 text-xs text-bruise">{hint}</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          aria-label={`${title}: ${targetLabel}`}
          placeholder={placeholder}
          value={current}
          onChange={(e) => setValue(e.target.value)}
          className={`${input} font-mono`}
        />
        <input
          aria-label={`${title}: reason`}
          placeholder="reason (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={input}
        />
      </div>
      <button type="submit" disabled={revoke.isPending} className={`${danger} mt-2`}>
        {revoke.isPending ? 'Revoking…' : title}
      </button>
      {done && <p className="mt-2">{done}</p>}
      <Problem text={problem} />
    </form>
  );
}

function RevokeBatchForm({ batchId, onRevoked }: { batchId: string; onRevoked: () => void }) {
  const { client } = useApi();
  return (
    <RevokeForm
      title="Revoke a batch"
      hint="Retires every card of a batch (pick one from the table's batch link, or paste its id)."
      target={batchId}
      targetLabel="batch id"
      placeholder="batch id"
      confirmText={(id) => `Revoke every working code in batch ${id}? This can't be undone.`}
      run={async (id, reason) =>
        ok(
          await client.POST('/admin/trigger-codes/revoke-batch', { body: { batchId: id, reason } }),
        )
      }
      onRevoked={onRevoked}
    />
  );
}

function RevokeSeasonForm({ season, onRevoked }: { season: string; onRevoked: () => void }) {
  const { client } = useApi();
  return (
    <RevokeForm
      title="Revoke a season"
      hint="Retires every code of that season. Live missions get fresh web codes on the next Missions view."
      target=""
      targetLabel="season"
      placeholder={season ? `season, e.g. ${season}` : 'season'}
      confirmText={(s) => `Revoke every working code in season "${s}"? This can't be undone.`}
      run={async (s, reason) =>
        ok(await client.POST('/admin/trigger-codes/revoke-season', { body: { season: s, reason } }))
      }
      onRevoked={onRevoked}
    />
  );
}

type Editing = { id: string; action: 'window' | 'revoke' };

function CodesTable({
  rows,
  triggers,
  onChanged,
  onBatch,
}: {
  rows: Row[];
  triggers: TriggerOption[];
  onChanged: () => void;
  onBatch: (batchId: string) => void;
}) {
  const [open, setOpen] = useState<Editing | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  if (rows.length === 0) return <p className="text-sm">No codes match.</p>;

  const copy = async (r: Row) => {
    try {
      await navigator.clipboard.writeText(r.scanUrl);
      setCopied(r.id);
      setProblem(null);
    } catch {
      setProblem(`Could not copy; the URL is ${r.scanUrl}`);
    }
  };
  const fail = (e: unknown) => setProblem(errorText(e));
  const svg = (r: Row) =>
    void qrSvg(r.scanUrl)
      .then((s) => downloadBlob(new Blob([s], { type: 'image/svg+xml' }), `${r.code}.svg`))
      .catch(fail);
  const png = (r: Row) =>
    void qrPngDataUrl(r.scanUrl)
      .then(async (url) => downloadBlob(await (await fetch(url)).blob(), `${r.code}.png`))
      .catch(fail);
  const poster = (r: Row) =>
    void qrSvg(r.scanUrl)
      .then((s) => {
        const def = triggers.find((t) => t.value === r.triggerId)?.def;
        const html = posterHtml({
          title: def?.title || r.triggerTitle,
          description: def?.description,
          code: r.code,
          scanUrl: r.scanUrl,
          svg: s,
        });
        const w = window.open('', '_blank');
        if (!w) {
          downloadBlob(new Blob([html], { type: 'text/html' }), `${r.code}-poster.html`);
          return;
        }
        w.document.write(html);
        w.document.close();
        w.focus();
        w.print();
      })
      .catch(fail);

  const headers = [
    'Code',
    'Trigger',
    'Channel',
    'Season',
    'Window',
    'Uses',
    'State',
    'Scans',
    'Redeemed',
    'Refused',
    'Created',
    '',
  ];

  return (
    <>
      <Problem text={problem} />
      <table className="w-full border-2 border-ink bg-white text-sm">
        <thead className="bg-ink text-newsprint">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-2 py-1 text-left">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const editing = open?.id === r.id ? open.action : null;
            const toggle = (action: Editing['action']) =>
              setOpen(editing === action ? null : { id: r.id, action });
            const done = () => {
              setOpen(null);
              onChanged();
            };
            return (
              <Fragment key={r.id}>
                <tr className="border-t border-newsprint align-top" data-testid={`code-${r.code}`}>
                  <td className="px-2 py-1 font-mono">{r.code}</td>
                  <td className="px-2 py-1">
                    {r.triggerTitle}
                    <div className="font-mono text-xs text-bruise">
                      {r.action}
                      {r.kind ? `:${r.kind}` : ''}
                    </div>
                  </td>
                  <td className="px-2 py-1 font-mono">
                    {r.channel}
                    {r.batchId && (
                      <button
                        type="button"
                        className="ml-1 text-xs underline"
                        onClick={() => onBatch(r.batchId!)}
                      >
                        batch
                      </button>
                    )}
                    {r.geofence && (
                      <div className="text-xs text-bruise">
                        within {r.geofence.radiusM} m of {r.geofence.lat.toFixed(5)},{' '}
                        {r.geofence.lon.toFixed(5)}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1 font-mono">{r.season}</td>
                  <td className="px-2 py-1 text-xs">{windowText(r)}</td>
                  <td className="px-2 py-1 text-right font-mono">
                    {r.redemptionCount}/{r.maxRedemptions ?? '∞'}
                  </td>
                  <td className="px-2 py-1">
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-semibold ${stateCls[r.state] ?? 'bg-newsprint'}`}
                      title={r.revokeReason ? `Revoked: ${r.revokeReason}` : undefined}
                    >
                      {r.state}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">{r.scans}</td>
                  <td className="px-2 py-1 text-right">{r.redemptions}</td>
                  <td className="px-2 py-1 text-right">{r.refusals}</td>
                  <td className="px-2 py-1 text-xs">
                    {r.createdBy ?? 'system'}
                    <br />
                    {when(r.createdAt)}
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex flex-wrap gap-1">
                      <button type="button" className={btn} onClick={() => void copy(r)}>
                        {copied === r.id ? 'Copied' : 'Copy URL'}
                      </button>
                      <button type="button" className={btn} onClick={() => svg(r)}>
                        QR SVG
                      </button>
                      <button type="button" className={btn} onClick={() => png(r)}>
                        QR PNG
                      </button>
                      <button type="button" className={btn} onClick={() => poster(r)}>
                        Poster
                      </button>
                      <button type="button" className={btn} onClick={() => toggle('window')}>
                        Edit
                      </button>
                      {!r.revokedAt && (
                        <button type="button" className={btn} onClick={() => toggle('revoke')}>
                          Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {editing && (
                  <tr className="bg-newsprint/40">
                    <td colSpan={headers.length} className="px-2 py-2">
                      {editing === 'window' ? (
                        <WindowEditor row={r} onDone={done} />
                      ) : (
                        <RevokeEditor row={r} onDone={done} />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/** Replaces the code's window, max uses and geofence together (the API's PUT). */
function WindowEditor({ row, onDone }: { row: Row; onDone: () => void }) {
  const { client } = useApi();
  const [form, setForm] = useState({
    activeFrom: isoToLocalInput(row.activeFrom),
    activeUntil: isoToLocalInput(row.activeUntil),
    maxUses: row.maxRedemptions == null ? '' : String(row.maxRedemptions),
    geo: geoText(row.geofence),
    reason: '',
  });
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (body: Schemas['AdminTriggerCodeWindow']) =>
      ok(
        await client.PUT('/admin/trigger-codes/{id}/window', {
          params: { path: { id: row.id } },
          body,
        }),
      ),
    onSuccess: onDone,
    onError: (e) => setProblem(errorText(e)),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const [maxRedemptions, maxProblem] = parseOptional(form.maxUses, 'Max uses', true);
        if (maxProblem) return setProblem(maxProblem);
        const [geofence, geoProblem] = parseGeo(form.geo);
        if (geoProblem) return setProblem(geoProblem);
        save.mutate({
          activeFrom: localInputToIso(form.activeFrom),
          activeUntil: localInputToIso(form.activeUntil),
          maxRedemptions,
          geofence,
          reason: form.reason || null,
        });
      }}
      className="grid max-w-3xl grid-cols-2 gap-2 text-sm"
    >
      <WindowInputs
        label={row.code}
        from={form.activeFrom}
        until={form.activeUntil}
        onChange={(activeFrom, activeUntil) => setForm({ ...form, activeFrom, activeUntil })}
      />
      <label className="flex items-center gap-2">
        Max uses
        <input
          aria-label={`${row.code} max uses`}
          placeholder="no cap"
          value={form.maxUses}
          onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
          className={`${input} w-24 font-mono`}
        />
        <span className="text-xs text-bruise">used {row.redemptionCount}</span>
      </label>
      <input
        aria-label={`${row.code} window reason`}
        placeholder="reason"
        value={form.reason}
        onChange={(e) => setForm({ ...form, reason: e.target.value })}
        className={input}
      />
      <GeoInputs label={row.code} value={form.geo} onChange={(geo) => setForm({ ...form, geo })} />
      <div className="col-span-2 flex items-center gap-2">
        <button type="submit" disabled={save.isPending} className={primary}>
          Save
        </button>
        <span className="text-xs text-bruise">
          Empty = no limit. Saves window, max uses and geofence together.
        </span>
      </div>
      <div className="col-span-2">
        <Problem text={problem} />
      </div>
    </form>
  );
}

function RevokeEditor({ row, onDone }: { row: Row; onDone: () => void }) {
  const { client } = useApi();
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: async () =>
      ok(
        await client.POST('/admin/trigger-codes/{id}/revoke', {
          params: { path: { id: row.id } },
          body: { reason },
        }),
      ),
    onSuccess: onDone,
    onError: (e) => setProblem(errorText(e)),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!reason.trim()) return setProblem('A reason is required.');
        if (!confirm(`Revoke ${row.code}? Scans will say the code is retired.`)) return;
        revoke.mutate();
      }}
      className="flex flex-wrap items-center gap-2 text-sm"
    >
      <input
        aria-label={`${row.code} revoke reason`}
        placeholder="reason (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className={`${input} w-80`}
      />
      <button type="submit" disabled={revoke.isPending} className={danger}>
        Revoke {row.code}
      </button>
      {row.channel === 'web' && (
        <span className="text-xs text-bruise">
          A live mission gets a new web code on the next Missions view.
        </span>
      )}
      <div className="w-full">
        <Problem text={problem} />
      </div>
    </form>
  );
}
